import axios, { AxiosRequestConfig, AxiosInstance } from "axios";
import { stores } from "../index";
import { Cookies } from "react-cookie";

export const DEFAULT_TIMEOUT = 20000; // 20 seconds

// Set global default timeout for any direct axios calls
axios.defaults.timeout = DEFAULT_TIMEOUT;

export default class RestService {
  client: AxiosInstance;
  constructor(config: AxiosRequestConfig) {
    this.client = axios.create({
      timeout: DEFAULT_TIMEOUT,
      ...config,
    });
    this.client.interceptors.request.use(
      async (config) => {
        const token = await getToken();
        // console.log("token---", token);

        if (token && !!config.headers) {
          config.headers["Authorization"] = "Bearer " + token;
        }
        // console.log('Request: ', config.url, config.headers?.Authorization);
        return config;
      },
      (error) => {
        // console.log(error);
        return Promise.reject(error);
      },
    );

    this.client.interceptors.response.use(
      async (response) => {
        if (response?.data?.accessToken) {
          await setToken(response?.data?.accessToken);
          this.client.defaults.headers.common["Authorization"] =
            `Bearer ` + response?.data?.accessToken;
        }
        return response;
      },
      async (error) => {
        const originalRequest = error?.config as (AxiosRequestConfig & {
          _retry?: boolean;
          _retried?: boolean;
          _noRetry?: boolean;
        }) | undefined;
        // console.log(error);

        // Check if request timed out (>20 seconds) on the first attempt
        const isTimeout =
          error?.code === "ECONNABORTED" ||
          (typeof error?.message === "string" &&
            error.message.toLowerCase().includes("timeout"));

        if (
          isTimeout &&
          originalRequest &&
          !originalRequest._retried &&
          !originalRequest._noRetry
        ) {
          originalRequest._retried = true;
          // For the second call, remove the time limit (0 = infinite / no limit in axios)
          originalRequest.timeout = 0;

          console.warn(
            `[RestService] API call to ${originalRequest.url || "endpoint"} timed out after 20s. Recalling API with no time limit...`
          );

          // Brief delay to release stuck connection/sockets
          await new Promise((resolve) => setTimeout(resolve, 500));
          return this.client(originalRequest);
        }

        if (error?.response?.status === 405 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;
          const newToken = error?.response?.data?.accessToken;
          if (newToken) {
            await setToken(newToken);
            this.client.defaults.headers.common["Authorization"] = "Bearer " + newToken;
            if (originalRequest.headers) {
              originalRequest.headers["Authorization"] = "Bearer " + newToken;
            }
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return this.client(originalRequest);
          }
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return this.client(originalRequest);
        }

        return Promise.reject(error);
      },
    );
  }

  get(endpoint: string, config?: AxiosRequestConfig) {
    return this.client.get<any>(endpoint, config);
  }

  post(endpoint: string, payload: any, config?: AxiosRequestConfig) {
    return this.client.post<any>(endpoint, payload, config);
  }

  put(endpoint: string, payload: any, config?: AxiosRequestConfig) {
    return this.client.put<any>(endpoint, payload, config);
  }
  postwithHeader(endpoint: string, payload: any, header: any) {
    return this.client.post<any>(endpoint, payload, header);
  }
  postWithConfig(
    endpoint: string,
    payload: any,
    config: AxiosRequestConfig<any> | undefined,
  ) {
    return this.client.post<any>(endpoint, payload, config);
  }
  delete(endpoint: string, config?: AxiosRequestConfig) {
    return this.client.delete<any>(endpoint, config);
  }
}

const getToken = async (): Promise<string | null | undefined> => {
  return stores.getState().user?.userDetails?.accessToken;
};

const setToken = async (token: string) => {
  const state = stores.getState();
  const currentUserDetails = state.user?.userDetails;
  if (currentUserDetails) {
    const updatedUserDetails = {
      ...currentUserDetails,
      accessToken: token,
    };
    stores.dispatch({
      type: "[USER] User Login Success Action",
      payload: updatedUserDetails,
    });

    try {
      const cook = new Cookies();
      cook.set("user", updatedUserDetails, { path: "/", maxAge: 30 * 24 * 60 * 60 });
      localStorage.setItem("user", JSON.stringify(updatedUserDetails));
    } catch (e) {
      console.error("Error setting user cookie/localStorage:", e);
    }
    try {
      localStorage.setItem("user", JSON.stringify(updatedUserDetails));
    } catch (e) {
      console.error("Error setting user in localStorage:", e);
    }
  }
};
